import { z } from 'zod';

import { businessResponseSchema, resolutionSchema } from './response';
import { publicUserSchema } from './user';

export const postTypeSchema = z.enum(['GOOD', 'BAD', 'IDEAL']);
export const postScopeSchema = z.enum(['TOWN', 'COUNTRY', 'LIFE']);
export const mediaKindSchema = z.enum(['IMAGE', 'VIDEO']);

export const mediaSchema = z.object({
  id: z.string(),
  kind: mediaKindSchema,
  url: z.string(),
  smallUrl: z.string().nullable(),
  largeUrl: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  blurhash: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  durationSec: z.number().nullable(),
  mimeType: z.string(),
  order: z.number(),
});

export const linkedPostSchema = z.object({
  id: z.string(),
  type: postTypeSchema,
  title: z.string().nullable(),
  body: z.string(),
  upvoteCount: z.number(),
});

export const postSchema = z.object({
  id: z.string(),
  type: postTypeSchema,
  scope: postScopeSchema,
  title: z.string().nullable(),
  body: z.string(),
  town: z.string().nullable(),
  countryCode: z.string().nullable(),
  countryFlag: z.string().nullable(),
  countryName: z.string().nullable(),
  isAnonymous: z.boolean(),
  author: publicUserSchema.nullable(),
  media: z.array(mediaSchema),
  tags: z.array(z.string()),
  upvoteCount: z.number(),
  commentCount: z.number(),
  idealCount: z.number(),
  hasUpvoted: z.boolean(),
  hasBookmarked: z.boolean(),
  isIdealOfMonth: z.boolean(),
  canEdit: z.boolean(),
  canDelete: z.boolean(),
  linkedPost: linkedPostSchema.nullable(),
  // Usually empty: only a post a business answered carries either of these.
  officialResponses: z.array(businessResponseSchema),
  resolutions: z.array(resolutionSchema),
  status: z.enum(['PUBLISHED', 'HIDDEN', 'REMOVED']),
  isEdited: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const postPageSchema = z.object({
  items: z.array(postSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

export const postListSchema = z.array(postSchema);

export const uploadUrlSchema = z.object({
  mediaId: z.string(),
  uploadUrl: z.string(),
  contentType: z.string(),
  kind: mediaKindSchema,
  expiresInSeconds: z.number(),
});

export type Post = z.infer<typeof postSchema>;
export type PostPage = z.infer<typeof postPageSchema>;
export type Media = z.infer<typeof mediaSchema>;
export type PostType = z.infer<typeof postTypeSchema>;
export type PostScope = z.infer<typeof postScopeSchema>;
export type UploadUrl = z.infer<typeof uploadUrlSchema>;
